import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ChevronLeft, Tag, Eye } from 'lucide-react';
import { useArticles } from '../hooks/useArticles';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 3rem 0;
  position: relative;
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  font-weight: 500;
  transition: var(--transition);

  &:hover {
    color: var(--white);
  }
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 2.5rem;
  color: var(--white);
  font-weight: 800;
  max-width: 900px;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ArticleWrapper = styled.article`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const ArticleHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid var(--border-light);
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--accent-gold);
  color: var(--primary-blue-dark);
  padding: 0.4rem 1rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  color: var(--text-light);
  font-size: 0.95rem;

  svg {
    color: var(--primary-blue);
    opacity: 0.6;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ArticleContent = styled.div`
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  /* Styles pour le contenu HTML de Quill */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-secondary);
    color: var(--text-dark);
    margin: 1.5rem 0 1rem;
    font-weight: 700;
  }

  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: var(--text-medium);
    margin-bottom: 1.25rem;
  }

  ul, ol {
    margin: 1.25rem 0;
    padding-left: 2rem;
  }

  li {
    font-size: 1.1rem;
    line-height: 1.8;
    color: var(--text-medium);
    margin-bottom: 0.5rem;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-lg);
    margin: 1.5rem 0;
    box-shadow: var(--shadow-md);
  }

  a {
    color: var(--primary-blue);
    text-decoration: underline;

    &:hover {
      color: var(--secondary-red);
    }
  }

  blockquote {
    border-left: 4px solid var(--accent-gold);
    padding-left: 1.5rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: var(--text-dark);
  }

  strong {
    color: var(--text-dark);
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s {
    text-decoration: line-through;
  }

  /* Alignement */
  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  .ql-align-justify {
    text-align: justify;
  }
`;

const LoadingState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  color: var(--text-light);
`;

const ErrorState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  
  h2 {
    color: var(--text-dark);
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const ArticleDetail = () => {
  const { id } = useParams();
  const { articles, loading } = useArticles();

  if (loading) {
    return (
      <PageContainer>
        <Hero>
          <HeroContent>
            <LoadingState>Chargement...</LoadingState>
          </HeroContent>
        </Hero>
      </PageContainer>
    );
  }

  const article = articles.find(a => a.id === id);

  if (!article) {
    return <Navigate to="/actualites" replace />;
  }

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <BackLink to="/actualites">
            <ChevronLeft size={20} />
            Retour aux actualités
          </BackLink>
          <HeroTitle>{article.title}</HeroTitle>
        </HeroContent>
      </Hero>

      <Container>
        <ArticleWrapper>
          {article.featuredImage && (
            <FeaturedImage>
              <img src={article.featuredImage} alt={article.title} />
            </FeaturedImage>
          )}

          <ArticleHeader>
            <CategoryBadge>
              <Tag size={14} />
              {article.category}
            </CategoryBadge>
            <MetaInfo>
              <MetaItem>
                <Calendar size={16} />
                {article.createdAt.toLocaleDateString('fr-FR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </MetaItem>
              <MetaItem>
                <Clock size={16} />
                {article.readTime || '3 min'}
              </MetaItem>
              <MetaItem>
                <Eye size={16} />
                {article.views || 0} vues
              </MetaItem>
            </MetaInfo>
          </ArticleHeader>

          <ArticleContent 
            dangerouslySetInnerHTML={{ __html: article.content }} 
          />
        </ArticleWrapper>
      </Container>
    </PageContainer>
  );
};

export default ArticleDetail;
